import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface AdjustModalResult {
  billableAcres: number;
  pricePerAcre: number;
  reason: string;
}

/**
 * Adjust-payment modal with live fee-recalc preview.
 *
 * Spec: /agent-os/specs/2026-06-10-deferred-capture-admin-approval/spec.md (§ 5.3.1 detail page)
 *
 * Admin types in new billable acres + per-acre rate; the preview pane
 * mirrors the Hub-side `recalculateFeesFromAcres` math so the admin
 * sees exactly what will be captured BEFORE submitting. The constants
 * `FARMER_FEE_PERCENTAGE` + `PILOT_FEE_PERCENTAGE` are duplicated here
 * (intentionally — these are display-only); the source of truth lives
 * server-side in `marketplace-fees.ts`, and the Hub re-runs the math
 * server-side at submit time. If the constants ever drift, the
 * server's value wins.
 *
 * Reason field is required — every adjustment goes into the revision
 * timeline + the auditor needs the human explanation for why the
 * numbers moved.
 */
@Component({
  selector: 'app-adjust-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="modal-backdrop fade show" (click)="onCancel()"></div>
    <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-pencil me-2"></i>Adjust payment
            </h5>
            <button type="button" class="btn-close" (click)="onCancel()" [disabled]="processing"></button>
          </div>

          <div class="modal-body">
            <p class="text-muted small">
              Update billable acres and/or the per-acre rate. Fees are
              recomputed from acres × rate. The row stays in
              <em>Pending approval</em> after submit — capture happens
              when an admin approves.
            </p>

            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Billable acres</label>
                <input
                  type="number"
                  class="form-control"
                  [(ngModel)]="billableAcres"
                  step="0.01"
                  min="0.01"
                  [disabled]="processing"
                  name="billableAcres" />
                <small class="text-muted">Was: {{ currentBillableAcres.toFixed(2) }}</small>
              </div>
              <div class="col-md-6">
                <label class="form-label">Per-acre rate ($)</label>
                <input
                  type="number"
                  class="form-control"
                  [(ngModel)]="pricePerAcre"
                  step="0.01"
                  min="0.01"
                  [disabled]="processing"
                  name="pricePerAcre" />
                <small class="text-muted">Was: \${{ currentPricePerAcre.toFixed(2) }}</small>
              </div>
            </div>

            <!-- Live preview -->
            <!--
              bg-body-tertiary (Bootstrap 5.3+) auto-adapts to the
              active data-bs-theme — light theme gets a subtle gray,
              dark theme gets a slightly-lighter-than-body dark surface.
              The legacy bg-light was hard-coded white and made the
              preview unreadable against the dark sidebar/body. (Stg
              2026-06-11.)
            -->
            <div class="mt-3 p-3 bg-body-tertiary rounded">
              <div class="text-muted small mb-2">Preview (server will re-verify on submit)</div>
              @if (preview) {
                <div class="row g-2 small">
                  <div class="col-6 col-md-3">
                    <div class="text-muted">Gross</div>
                    <div class="fw-semibold">{{ formatMoney(preview.grossAmount) }}</div>
                  </div>
                  <div class="col-6 col-md-3">
                    <div class="text-muted">+ 5% Marketplace Fee</div>
                    <div class="fw-semibold">{{ formatMoney(preview.farmerSurchargeAmount) }}</div>
                  </div>
                  <div class="col-6 col-md-3">
                    <div class="text-muted">Farmer charged</div>
                    <div class="fw-semibold text-primary">{{ formatMoney(preview.totalChargeAmount) }}</div>
                  </div>
                  <div class="col-6 col-md-3">
                    <div class="text-muted">Pilot payout</div>
                    <div class="fw-semibold text-success">{{ formatMoney(preview.pilotPayoutAmount) }}</div>
                  </div>
                </div>
                <div class="border-top mt-2 pt-2 small text-muted d-flex justify-content-between">
                  <span>Δ from current total</span>
                  <span [class.text-success]="(preview.totalChargeAmount - currentTotalCharge) < 0"
                        [class.text-danger]="(preview.totalChargeAmount - currentTotalCharge) > 0">
                    {{ formatDelta(preview.totalChargeAmount - currentTotalCharge) }}
                  </span>
                </div>
              } @else {
                <div class="text-muted fst-italic small">Enter positive values to see the preview.</div>
              }
            </div>

            <div class="mt-3">
              <label class="form-label">
                Reason <span class="text-danger">*</span>
              </label>
              <textarea
                class="form-control"
                rows="2"
                [(ngModel)]="reason"
                placeholder="e.g. Pilot conceded 0.1 ac by phone, both parties agreed"
                [disabled]="processing"
                name="reason"></textarea>
              <small class="text-muted">This goes into the revision timeline for audit.</small>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" (click)="onCancel()" [disabled]="processing">
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              (click)="onConfirm()"
              [disabled]="processing || !canSubmit">
              @if (processing) {
                <span class="spinner-border spinner-border-sm me-2"></span>
                Applying...
              } @else {
                Apply adjustment
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class AdjustModalComponent {
  @Input() currentBillableAcres = 0;
  @Input() currentPricePerAcre = 0;
  @Input() currentTotalCharge = 0;
  @Input() processing = false;

  @Output() confirmed = new EventEmitter<AdjustModalResult>();
  @Output() cancelled = new EventEmitter<void>();

  private readonly FARMER_FEE_PERCENTAGE = 0.05;
  private readonly PILOT_FEE_PERCENTAGE = 0.05;

  billableAcres = 0;
  pricePerAcre = 0;
  reason = '';

  ngOnInit(): void {
    this.billableAcres = this.currentBillableAcres;
    this.pricePerAcre = this.currentPricePerAcre;
  }

  get preview(): {
    grossAmount: number;
    farmerSurchargeAmount: number;
    totalChargeAmount: number;
    pilotPayoutAmount: number;
  } | null {
    if (
      !Number.isFinite(this.billableAcres) ||
      !Number.isFinite(this.pricePerAcre) ||
      this.billableAcres <= 0 ||
      this.pricePerAcre <= 0
    ) {
      return null;
    }
    const grossAmount = this.roundCents(this.billableAcres * this.pricePerAcre);
    const farmerSurchargeAmount = this.roundCents(grossAmount * this.FARMER_FEE_PERCENTAGE);
    const totalChargeAmount = this.roundCents(grossAmount + farmerSurchargeAmount);
    const pilotFeeDeductionAmount = this.roundCents(grossAmount * this.PILOT_FEE_PERCENTAGE);
    const pilotPayoutAmount = this.roundCents(grossAmount - pilotFeeDeductionAmount);
    return { grossAmount, farmerSurchargeAmount, totalChargeAmount, pilotPayoutAmount };
  }

  get canSubmit(): boolean {
    return !!this.preview && this.reason.trim().length > 0;
  }

  onConfirm(): void {
    if (!this.canSubmit) return;
    this.confirmed.emit({
      billableAcres: this.billableAcres,
      pricePerAcre: this.pricePerAcre,
      reason: this.reason.trim(),
    });
  }

  onCancel(): void {
    if (!this.processing) this.cancelled.emit();
  }

  formatMoney(v: number): string {
    return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  formatDelta(v: number): string {
    const sign = v >= 0 ? '+' : '';
    return sign + v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  /** Mirror of marketplace-fees.ts `roundCents` (epsilon-nudge defense). */
  private roundCents(value: number): number {
    return Math.round(value * 100 * (1 + Number.EPSILON)) / 100;
  }
}
